import { Component, HostBinding, ViewEncapsulation, inject, input, effect } from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';

import { ApContentModule, ApContentService } from '@ap/content';
import { currencyTransformer } from '@ap/formatters';

import formErrorsContent from './form-errors.content.json';
import { ErrorMessages } from './error-messages.interface';
import { ApErrorMessageService } from './error-messages.service';

@Component({
    selector: 'ap-form-errors',
    templateUrl: './form-errors.component.html',
    styleUrls: ['./form-errors.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ApContentModule],
    providers: [ApErrorMessageService]
})
export class ApFormErrorsComponent {
    private readonly contentService = inject(ApContentService);
    private readonly errorMessageService = inject(ApErrorMessageService);

    @HostBinding('attr.role') protected role = 'alert';

    @HostBinding('attr.aria-live') protected ariaLive = 'assertive';

    /**
     * Error message object or array of strings.
     * If array of strings: First string is always shown on error
     * If error message object: Key that matches current error is shown.
     */
    public readonly messages = input<ErrorMessages | string[]>();

    /**
     * Error message object. Key that matches current error is shown.
     */
    public readonly control = input<NgControl | FormControl>();

    /**
     * Json object that contains default content messages from form element
     */
    public readonly content = input<{
        [key: string]: any;
    }>();

    /**
     * Default error messages from this input
     */
    private defaultMessages = {};

    constructor() {
        this.contentService.addContentItems(formErrorsContent);
        effect(() => this.setDefaultErrorMessages(this.content()));
    }

    /**
     * Get current errors
     * @ignore
     */
    public get error(): string {
        const { dirty, errors, invalid, touched } = this.control() || {};

        if (!errors || !dirty || !invalid || !touched) {
            return null;
        }

        // If messages array always show the first error
        const messages = this.messages();
        if (messages && Array.isArray(messages)) {
            return messages[0];
        }

        const combinedMessages = this.combinedMessages;

        if (!combinedMessages) {
            return null;
        }

        const errorKey = Object.keys(errors).find((error) => combinedMessages[error]) || 'incorrect';
        const params = errors[errorKey] ? { ...errors[errorKey] } : {};
        return combinedMessages[errorKey]
            ? this.contentService.get(combinedMessages[errorKey], this.formatParams(params))
            : combinedMessages.incorrect;
    }

    /**
     * Format known params for user readability
     */
    private formatParams(params: any): any {
        if (params.max) {
            params.max = currencyTransformer(params.max, 0);
        }
        if (params.min) {
            params.min = currencyTransformer(params.min, 0);
        }
        return params;
    }

    /**
     * Get error messages object from passed content
     */
    private setDefaultErrorMessages(content: { [key: string]: any }): void {
        if (!content) {
            this.defaultMessages = {};
            return;
        }
        this.defaultMessages = this.errorMessageService.parseFromContentObject(content);
    }

    /**
     * Get combined user and default messages
     */
    private get combinedMessages(): ErrorMessages {
        const defaultMessages = Object.assign({}, this.defaultMessages);
        return Object.assign(defaultMessages, this.messages() as ErrorMessages);
    }
}
